import { useApiClient } from '@/api/ApiClientProvider'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { ModelSelect } from '@/components/ModelSelect'
import { Card, LoadingBlock } from '@/design'
import { usePermissions } from '@/lib/access'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { AdvertisingFreshnessBadge, SourceBadge } from './AdvertisingBadges'
import { AdvertisingError, ConnectMeta, NoRows } from './AdvertisingStates'
import { AdvertisingTable, AdvertisingTotals } from './AdvertisingTable'
import {
  type AdvertisingReportResult,
  useAdvertisingQuery,
  useAdvertisingReport,
  useModel,
  useSearchTerm,
  useSort,
} from './advertisingQuery'
import { CsvButton } from './CsvButton'
import { isFlagOff } from './errors'
import { subtitleFor } from './metrics'
import './advertising.css'

/**
 * Analytics › Advertising: Meta campaign spend and delivery, by campaign, ad
 * set and ad. Purchases, revenue and ROAS are Meta's own attribution — badged
 * as such — beside TmT's own first-party attributed figures under a selectable
 * model (M2, docs/architecture/integrations-meta.md §12.6), visibly distinct.
 */
export function AdvertisingPage({ route }: { route: RouteDef }) {
  const client = useApiClient()
  const { can } = usePermissions()
  const { filters } = useGlobalFilters()
  const [sort, setSort] = useSort()
  const [search, setSearch] = useSearchTerm()
  const [model, setModel] = useModel()
  const query = useAdvertisingQuery(filters, sort, search, model)
  const report = useAdvertisingReport(query)
  const source = client.kind === 'http' ? 'live' : 'demo'
  usePageFreshness(report.data ? { source, lastUpdatedAt: report.data.asOf.generatedAt } : undefined)

  return (
    <SectionFrame route={route} actions={<SourceBadge />}>
      <div className="ci-stack">
        <div className="adv-controls">
          <label className="ci-field ci-field--inline">
            <span>Search</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Campaign name"
            />
          </label>
          <ModelSelect value={model} onChange={setModel} />
          {report.data ? <AdvertisingFreshnessBadge freshness={report.data.asOf} source={source} /> : null}
          {can('exports.create') && report.data && report.data.rows.length > 0 ? <CsvButton query={query} /> : null}
        </div>

        <AdvertisingBody report={report} query={query} sort={sort} onSort={setSort} />
      </div>
    </SectionFrame>
  )
}

function AdvertisingBody({
  report,
  query,
  sort,
  onSort,
}: {
  report: AdvertisingReportResult
  query: ReturnType<typeof useAdvertisingQuery>
  sort: string
  onSort: (next: string) => void
}) {
  if (report.isPending) return <LoadingBlock rows={6} label="Loading the Meta campaign report" />
  if (!report.data) {
    if (isFlagOff(report.error)) return <ConnectMeta reason="not_entitled" />
    return <AdvertisingError error={report.error} />
  }
  const data = report.data
  if (!data.asOf.connected || data.asOf.mappedAccounts === 0) return <ConnectMeta reason="not_connected" />
  if (data.rows.length === 0) return <NoRows from={data.start} to={data.end} />
  return (
    <Card title="Campaigns" subtitle={subtitleFor(data)}>
      <AdvertisingTotals report={data} />
      {data.tmtUnmatchedPaid.purchases > 0 ? (
        <p role="note" className="adv-note">
          {data.tmtUnmatchedPaid.purchases} TmT-attributed purchases came from paid clicks that
          matched no imported ad.
        </p>
      ) : null}
      <AdvertisingTable report={data} baseQuery={query} sort={sort} onSort={onSort} />
      {report.hasNextPage ? (
        <button
          type="button"
          className="ci-button"
          disabled={report.isFetchingNextPage}
          onClick={report.fetchNextPage}
        >
          {report.isFetchingNextPage ? 'Loading more…' : 'Load more campaigns'}
        </button>
      ) : null}
    </Card>
  )
}
